// src/modulos/Parametros/ModelsPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import useNextCode from '../../hooks/useNextCode';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const MODELS_COLLECTION = 'parameters/data/modelos';
const ITEMS_PER_PAGE = 10;

const ModelsPage = () => {
    const can = usePagePermissions('modelos');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [currentCode, setCurrentCode] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '', code: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteCheckResult, setDeleteCheckResult] = useState(null);
    const [checkingDelete, setCheckingDelete] = useState(false);

    // Hooks de Dados
    const {
        data: models,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(MODELS_COLLECTION, 'code');

    const {
        nextCode,
        codeLoading,
        codeError,
        refetchCode
    } = useNextCode(MODELS_COLLECTION, 'code');

    // ============================================================
    // 1. DEFINIR filteredModels ANTES dos callbacks que o usam
    // ============================================================
    const filteredModels = useMemo(() => {
        if (!models) return [];
        if (!searchTerm) return models;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return models.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearch) ||
            item.code?.toString().includes(searchTerm)
        );
    }, [models, searchTerm]);

    const totalFilteredItems = filteredModels.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentModels = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredModels.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredModels, currentPage]);

    // Efeito para Sincronizar Código no Form
    useEffect(() => {
        if (isModalOpen) {
            if (editingItem) {
                setCurrentCode(editingItem.code || '');
            } else if (nextCode !== null && nextCode !== undefined) {
                setCurrentCode(nextCode);
            } else {
                setCurrentCode('');
            }
        }
    }, [editingItem, nextCode, isModalOpen]);

    // Reset do Formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setIsModalOpen(false);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
        setCurrentCode('');

        if (!editingItem && codeError) {
            refetchCode();
        }
    }, [codeError, editingItem, refetchCode]);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setIsModalOpen(true);
        setNameInput(item.name || '');
        setFormError('');
        setFormSuccess('');
    }, []);

    // Verificação de uso do modelo antes de excluir
    const checkModelUsage = useCallback(async (modelId, modelCode, modelName) => {
        setCheckingDelete(true);
        setDeleteCheckResult(null);

        try {
            const { collection, query, where, getDocs } = require('firebase/firestore');
            const { db } = require('../../firebaseConfig');

            const productsRef = collection(db, 'products');
            const productsQuery = query(productsRef, where('modelId', '==', modelId));
            const productsSnapshot = await getDocs(productsQuery);

            const ordersRef = collection(db, 'production_orders');
            const ordersQuery = query(ordersRef, where('modelId', '==', modelId));
            const ordersSnapshot = await getDocs(ordersQuery);

            const usageInfo = {
                modelName: modelName,
                modelCode: modelCode,
                hasProducts: !productsSnapshot.empty,
                productsCount: productsSnapshot.size,
                hasOrders: !ordersSnapshot.empty,
                ordersCount: ordersSnapshot.size,
                canDelete: productsSnapshot.empty && ordersSnapshot.empty
            };

            setDeleteCheckResult(usageInfo);
            return usageInfo;
        } catch (error) {
            console.error('Erro ao verificar uso do modelo:', error);
            setDeleteCheckResult({
                error: true,
                message: 'Erro ao verificar se o modelo pode ser excluído'
            });
            return null;
        } finally {
            setCheckingDelete(false);
        }
    }, []);

    const handleDeleteClick = useCallback(async (item) => {
        const usageInfo = await checkModelUsage(item.id, item.code, item.name);

        if (usageInfo && usageInfo.canDelete) {
            setDeleteConfirm({ isOpen: true, id: item.id, name: item.name, code: item.code });
        } else if (usageInfo && !usageInfo.canDelete) {
            let message = `Não é possível excluir o modelo "${item.name}" (Código: ${item.code})\n\nMotivo: Este modelo está sendo utilizado em:\n`;
            if (usageInfo.hasProducts) {
                message += `- ${usageInfo.productsCount} produto(s)\n`;
            }
            if (usageInfo.hasOrders) {
                message += `- ${usageInfo.ordersCount} ordem(ns) de produção\n`;
            }
            message += `\nPara excluir, primeiro remova todas as referências a este modelo.`;
            alert(message);
        }
    }, [checkModelUsage]);

    // Confirmar Delete (AGORA filteredModels já está definido)
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Modelo "${name}" excluído com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '', code: null });
                refetchCode();

                // Usar filteredModels (já definido acima)
                const newFilteredCount = filteredModels.length - 1;
                const maxPage = Math.ceil(newFilteredCount / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newFilteredCount === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Modelo: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '', code: null });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar modelo. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '', code: null });
        } finally {
            setDeleteLoading(false);
            setDeleteCheckResult(null);
        }
    }, [deleteConfirm, deleteDocument, refetchCode, filteredModels, currentPage]);

    // Submissão do Formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O campo Modelo é obrigatório.");
            return;
        }

        if (!editingItem) {
            if (nextCode === null || codeLoading) {
                setFormError(codeError || "Aguarde o código ser gerado.");
                return;
            }
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            ...timestamps,
        };

        if (!editingItem) {
            data.code = nextCode;
        }

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Modelo editado com sucesso!' : 'Modelo adicionado com sucesso!');
                setTimeout(() => {
                    resetForm();
                    if (!editingItem) {
                        refetchCode();
                    }
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, nextCode, codeLoading, codeError, addDocument, updateDocument, resetForm, refetchCode]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Novo Modelo'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Modelo'}
            >
                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="codeInput">Código (Numérico Autogerado)</label>
                        <input
                            id="codeInput"
                            type="text"
                            value={currentCode || ''}
                            readOnly
                            className={styles.input}
                            disabled={!editingItem && codeLoading}
                        />
                        {(codeError && !editingItem) && <p className={styles.errorHint}>{codeError}</p>}
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="nameInput">
                            Modelo <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="nameInput"
                            type="text"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            placeholder="Nome do Modelo (Ex: Linha Clássica)"
                            required
                            className={styles.input}
                            disabled={formLoading}
                            autoFocus={!editingItem}
                        />
                    </div>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '', code: null })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja excluir o modelo "${deleteConfirm.name}" (Código: ${deleteConfirm.code})?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            >
                {deleteCheckResult && deleteCheckResult.hasProducts && (
                    <div className={`${styles.alert} ${styles.warning}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>⚠️</span>
                        Atenção: Este modelo está vinculado a {deleteCheckResult.productsCount} produto(s).
                    </div>
                )}
                {deleteCheckResult && deleteCheckResult.hasOrders && (
                    <div className={`${styles.alert} ${styles.warning}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>⚠️</span>
                        Atenção: Este modelo está vinculado a {deleteCheckResult.ordersCount} ordem(ns) de produção.
                    </div>
                )}
                {deleteCheckResult && !deleteCheckResult.hasProducts && !deleteCheckResult.hasOrders && !deleteCheckResult.error && (
                    <div className={`${styles.alert} ${styles.success}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>✓</span>
                        Este modelo não está sendo utilizado em nenhum produto ou ordem de produção.
                    </div>
                )}
            </ConfirmModal>

            {/* Page Header */}
            <PageHeader
                title="Modelos"
                actionText="Adicionar Modelo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por código ou nome do modelo..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={3} rows={ITEMS_PER_PAGE} />
                    ) : currentModels.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '🏷️'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum modelo cadastrado'
                            }
                            message={
                                searchTerm
                                    ? 'Nenhum resultado encontrado para sua busca.'
                                    : "Clique em '+ Adicionar Modelo' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '120px' }}>Código</th>
                                            <th>Modelo</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentModels.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td>
                                                    <span className={styles.badge}>{item.code}</span>
                                                </td>
                                                <td>
                                                    <div className={styles.conditionName}>
                                                        <strong>{item.name}</strong>
                                                        {item.createdAt && (
                                                            <span className={styles.rowMetadata}>
                                                                Criado: {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                                                            </span>
                                                        )}
                                                        {item.updatedAt && item.updatedAt !== item.createdAt && (
                                                            <span className={styles.rowMetadata}>
                                                                Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading || checkingDelete}
                                                        >
                                                            {checkingDelete ? 'Verificando...' : 'Deletar'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="modelos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ModelsPage;